import React from 'react';
import Result from './Result';
import NextLevel from './NextLevel';
import axios from 'axios';
import { DateTime } from 'luxon';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  Fade,
  Slide,
  LinearProgress} from '@mui/material';
import {
  Casino as DiceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  EmojiEvents as TrophyIcon,
  Backspace as BackspaceIcon,
  Send as SendIcon} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import './Guess.css';

// Styled components for modern design
const GameContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: theme.spacing(1),
  paddingTop: theme.spacing(8), // Reduced padding for navbar
  paddingBottom: theme.spacing(2), // Reduced bottom padding
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none'
  }
}));

const GameCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2), // Further reduced padding
  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
  border: '1px solid rgba(255,255,255,0.2)',
  maxWidth: 500, // Further reduced max width
  width: '100%',
  margin: theme.spacing(0.5), // Further reduced margin
  transition: 'all 0.3s ease',
  maxHeight: 'calc(100vh - 120px)', // More space for viewport
  overflowY: 'auto',
  '&:hover': {
    transform: 'translateY(-2px)', // Reduced hover effect
    boxShadow: '0 25px 50px rgba(0,0,0,0.15)'
  }
}));

const NumberDisplay = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(1.5), // Further reduced padding
  borderRadius: theme.spacing(2),
  fontSize: '1.8rem', // Further reduced font size
  fontWeight: 'bold',
  textAlign: 'center',
  fontFamily: 'monospace',
  letterSpacing: '0.2rem', // Further reduced letter spacing
  minHeight: '50px', // Further reduced height
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: 'inset 0 4px 8px rgba(0,0,0,0.2)',
  border: '2px solid rgba(255,255,255,0.2)',
  marginBottom: theme.spacing(1.5), // Further reduced margin
  animation: 'pulse 2s infinite'
}));

const NumberButton = styled(Button)(({ theme, variant }) => ({
  width: '65px',
  height: '65px',
  borderRadius: theme.spacing(2),
  fontSize: '1.5rem',
  fontWeight: 'bold',
  margin: 0,
  padding: 0,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: variant === 'number' 
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : variant === 'zero'
    ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  color: 'white',
  border: '2px solid rgba(255,255,255,0.25)',
  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
  position: 'relative',
  overflow: 'hidden',
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    margin: 0
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '0',
    height: '0',
    background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    transition: 'width 0.4s ease, height 0.4s ease',
  },
  '&:hover': {
    transform: 'translateY(-3px) scale(1.08)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.3)',
    borderColor: 'rgba(255,255,255,0.5)',
    '&::before': {
      width: '90px',
      height: '90px',
    }
  },
  '&:active': {
    transform: 'translateY(-1px) scale(1.04)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
  }
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: theme.spacing(2),
  padding: 0,
  fontSize: '0.9rem',
  fontWeight: 'bold',
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  minHeight: '65px',
  minWidth: '65px',
  margin: 0,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: variant === 'enter'
    ? 'linear-gradient(135deg, #48c78e 0%, #06d6a0 100%)'
    : variant === 'delete'
    ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  border: '2px solid rgba(255,255,255,0.25)',
  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    margin: 0,
    marginLeft: variant === 'delete' ? '0' : '4px',
    marginRight: variant === 'enter' ? '0' : '4px'
  },
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.3)',
    borderColor: 'rgba(255,255,255,0.5)',
  },
  '&:active': {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
  }
}));

class GuessLevelOne extends React.Component {
  state = {
    number: [],
    defaultText: '* *',
    instructions: [],
    secretNumber: Math.floor(Math.random() * 99) + 1,
    count: 8,
    keycode: '',
    level: 1,
    score: 0,
    status: '',
    lastNumber: 100,
    userName: '',
    rank: '',
    defaultUser: '',
    toprankers: [],
    isSubmitting: false // Add flag to prevent double submissions
  }

  componentDidMount() {
    // Add event listener for keyboard input
    document.addEventListener('keydown', this.keyDown);
  }

  componentWillUnmount() {
    // Remove event listener to prevent memory leaks
    document.removeEventListener('keydown', this.keyDown);
  }

  getToppers = () => {
   axios.get("/guess.json")
   .then(response => { 
     const fetchedResults = [];
     for(let key in response.data){
       fetchedResults.unshift(
         {
           ...response.data[key],
           id:key
         }
       )
     }
     this.setState({
       toprankers: fetchedResults.sort((a,b) => (b.score - a.score))
     });
   })
 }
  
  handleClick = (event) => {
    if(this.state.number.length <= this.state.level) {
    this.setState({
      defaultText: '',
      number: this.state.number.concat(event.target.id)
    });
    }
  }

  keyDown = (e) => {
    // Only handle keyboard input when user is playing the game (not entering name)
    if (this.state.userName === '' || this.state.status === 'won' || this.state.count === 0 || this.state.isSubmitting) return;
    
    e.preventDefault();
    const keyCode = e.keyCode || e.which;
    
    if(this.state.number.length <= this.state.level) {
      if(keyCode === 49) { // Key '1'
        this.setState({
          defaultText: '',
          number: this.state.number.concat(1),
          keycode: keyCode
        });
      }
      if(keyCode === 50) { // Key '2'
        this.setState({
          defaultText: '',
          number: this.state.number.concat(2),
          keycode: keyCode
        });
      }
      if(keyCode === 51) { // Key '3'
        this.setState({
          defaultText: '',
          number: this.state.number.concat(3),
          keycode: keyCode
        });
      }
      if(keyCode === 52) { // Key '4'
        this.setState({
          defaultText: '',
          number: this.state.number.concat(4),
          keycode: keyCode
        });
      }
      if(keyCode === 53) { // Key '5'
        this.setState({
          defaultText: '',
          number: this.state.number.concat(5),
          keycode: keyCode
        });
      }
      if(keyCode === 54) { // Key '6'
        this.setState({
          defaultText: '',
          number: this.state.number.concat(6),
          keycode: keyCode
        });
      }
      if(keyCode === 55) { // Key '7'
        this.setState({
          defaultText: '',
          number: this.state.number.concat(7),
          keycode: keyCode
        });
      }
      if(keyCode === 56) { // Key '8'
        this.setState({
          defaultText: '',
          number: this.state.number.concat(8),
          keycode: keyCode
        });
      }
      if(keyCode === 57) { // Key '9'
        this.setState({
          defaultText: '',
          number: this.state.number.concat(9),
          keycode: keyCode
        });
      }
      if(keyCode === 48) { // Key '0'
        this.setState({
          defaultText: '',
          number: this.state.number.concat(0),
          keycode: keyCode
        });
      }
    }
    
    if(keyCode === 8 || keyCode === 46) { // Backspace or Delete
      this.deleteNumber();
    }
    if(keyCode === 13 && this.state.number.length > 0 && !this.state.isSubmitting) { // Enter - only if there's a number and not already submitting
      this.submitAnswer();
    }
    
    // Update keycode for visual feedback
    this.setState({
      keycode: keyCode
    });
  }
  
  deleteNumber = () => {
    const index = this.state.number.length - 1;
    const newNumber = this.state.number.slice();
    newNumber.splice(index,1)
    this.setState({
      number: newNumber
    });
  }

  submitAnswer = () => {
    if(!this.state.number.length || this.state.isSubmitting) {
      if(!this.state.number.length) {
        alert("Number Should not be Empty");
      }
      return;
    }

    // Set submitting flag to prevent double submissions
    this.setState({ isSubmitting: true });

    setTimeout(() => {
      const newInstruction = [...this.state.instructions];
      const guessedNumber = parseInt(this.state.number.join(''));
      
      if(guessedNumber > this.state.secretNumber) {
        newInstruction.unshift("The Number is smaller than " + guessedNumber);
        this.setState({
          instructions: newInstruction,
          number: [],
          count: this.state.count - 1,
          isSubmitting: false
        });
      }
      else if(guessedNumber < this.state.secretNumber) {
        newInstruction.unshift("The Number is greater than " + guessedNumber);
        this.setState({
          instructions: newInstruction,
          number: [],
          count: this.state.count - 1,
          isSubmitting: false
        });
      }
      else if(guessedNumber === this.state.secretNumber) {
        // Calculate score: level * 10 * attempts left
        const levelScore = this.state.level * 10 * this.state.count;
        
        newInstruction.unshift(`🎉 Congratulations! You guessed it! +${levelScore.toLocaleString()} points!`);
        this.setState({
          instructions: newInstruction,
          number: [],
          status: 'won',
          defaultText: this.state.secretNumber,
          level: this.state.level + 1,
          score: this.state.score + levelScore,
          isSubmitting: false
        });
      }
    }, 100); // Small delay to prevent rapid fire submissions
  }

  newGame = () => {
    // Complete restart for new game (when restarting from game over or starting fresh)
    if(this.state.count === 0 || this.state.status === '' || this.state.level === 1) {
      this.setState({
        number: [],
        defaultText: '* *',
        instructions: [],
        secretNumber: Math.floor(Math.random() * 99) + 1,
        count: 8,
        status: '',
        lastNumber: 100,
        level: 1,
        score: 0,
        userName: this.state.userName, // Preserve username
        rank: '',
        defaultUser: this.state.defaultUser, // Preserve defaultUser
        toprankers: this.state.toprankers, // Preserve toprankers
        isSubmitting: false
      });
      return; // Exit early to prevent other conditions from executing
    }

    // Level 2 progression - preserve score (only when won)
    if(this.state.level === 2 && this.state.status === 'won') {
      this.setState({
        number: [],
        defaultText: '* * *',
        instructions: [],
        secretNumber: Math.floor(Math.random() * 999) + 1,
        count: 9,
        status: '',
        lastNumber: 1000
      });
    }

  if(this.state.level === 3 && this.state.status === 'won') {
    this.setState({
    number: [],
    defaultText: '* * * *',
    instructions: [],
    secretNumber: Math.floor(Math.random() * 9999) + 1,
    count: 10,
    status: '',
    lastNumber: 10000
    });
  }

  if(this.state.level === 4 && this.state.status === 'won') {
    this.setState({
    number: [],
    defaultText: '* * * * *',
    instructions: [],
    secretNumber: Math.floor(Math.random() * 99999) + 1,
    count: 11,
    status: '',
    lastNumber: 100000
    });
  }

  if(this.state.level === 5 && this.state.status === 'won') {
    this.setState({
    number: [],
    defaultText: '* * * * * *',
    instructions: [],
    secretNumber: Math.floor(Math.random() * 999999) + 1,
    count: 12,
    status: '',
    lastNumber: 1000000
    });
  }

  if(this.state.level === 6 && this.state.status === 'won') {
    this.setState({
    number: [],
    defaultText: '* * * * * * *',
    instructions: [],
    secretNumber: Math.floor(Math.random() * 9999999) + 1,
    count: 13,
    status: '',
    lastNumber: 10000000
    });
  }

  if(this.state.level === 7 && this.state.status === 'won') {
    this.setState({
    number: [],
    defaultText: '* * * * * * * *',
    instructions: [],
    secretNumber: Math.floor(Math.random() * 99999999) + 1,
    count: 14,
    status: '',
    lastNumber: 100000000
    });
  }

  if(this.state.level === 8 && this.state.status === 'won') {
    this.setState({
    number: [],
    defaultText: '* * * * * * * * *',
    instructions: [],
    secretNumber: Math.floor(Math.random() * 999999999) + 1,
    count: 15,
    status: '',
    lastNumber: 1000000000
    });
  }

  if(this.state.level === 9 && this.state.status === 'won') {
    this.setState({
    number: [],
    defaultText: '* * * * * * * * * *',
    instructions: [],
    secretNumber: Math.floor(Math.random() * 9999999999) + 1,
    count: 16,
    status: '',
    lastNumber: 10000000000
    });
  }
  }
 
 inputUserName = () => {
   this.setState({
     userName: this.state.defaultUser
   });
 }

 handleChange = (e) => {
   this.setState({
     defaultUser: e.target.value
   })
 }

 // Auto-save score and fetch toppers when game ends
 componentDidUpdate(prevProps, prevState) {
   if (prevState.count > 0 && this.state.count === 0) {
     // Game just ended, auto-save score and fetch toppers
     this.saveScoreAndGetToppers();
   }
 }

 saveScoreAndGetToppers = () => {
   const now = DateTime.now();
   const formattedDate = now.toFormat('dd-MM-yyyy HH:mm:ss');

   const playerData = {
     userName: this.state.userName,
     score: this.state.score,
     rank: this.state.rank,
     time: formattedDate,
   }
   axios.post("/guess.json", playerData)
   .then(response => console.log('Score saved successfully'))
   .then(this.getToppers)
   .catch(error => console.log(error));
 }

  render() {
    const { number, defaultText, instructions, count, level, lastNumber, userName, 
            secretNumber, score, status, toprankers } = this.state;

    // User Name Input Component - using regular function to preserve 'this' context
    const renderUserNameDialog = () => (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, color: '#333' }}>
          🎮 Welcome to the Game!
        </Typography>
        <TextField
          fullWidth
          label="Enter Your Name"
          placeholder="Your awesome name here..."
          value={this.state.defaultUser}
          onChange={this.handleChange}
          sx={{ 
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              '&:hover fieldset': {
                borderColor: '#667eea'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#667eea'
              }
            }
          }}
        />
        <ActionButton
          fullWidth
          variant="start"
          onClick={this.inputUserName}
          startIcon={<DiceIcon />}
        >
          Start Playing!
        </ActionButton>
      </Box>
    );

    // Number Pad Component
    const NumberPad = () => (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        width: '100%',
        px: 2
      }}>
        {/* Professional Number Grid - 4 buttons per row */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1.5,
          width: 'fit-content',
          mb: 2,
          p: 2.5,
          background: 'rgba(255,255,255,0.08)',
          borderRadius: 4,
          border: '1px solid rgba(255,255,255,0.15)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          justifyItems: 'center',
          alignItems: 'center',
          margin: '0 auto'
        }}>
          {/* Row 1: 1, 2, 3 */}
          {[1, 2, 3].map((num) => (
            <NumberButton
              key={num}
              variant="number"
              id={num.toString()}
              onClick={this.handleClick}
              sx={{
                border: this.state.keycode === (48 + num).toString() ? '3px solid #ffd700' : undefined
              }}
            >
              {num}
            </NumberButton>
          ))}
          
          {/* Row 2: 4, 5, 6 */}
          {[4, 5, 6].map((num) => (
            <NumberButton
              key={num}
              variant="number"
              id={num.toString()}
              onClick={this.handleClick}
              sx={{
                border: this.state.keycode === (48 + num).toString() ? '3px solid #ffd700' : undefined
              }}
            >
              {num}
            </NumberButton>
          ))}
          
          {/* Row 3: 7, 8, 9 */}
          {[7, 8, 9].map((num) => (
            <NumberButton
              key={num}
              variant="number"
              id={num.toString()}
              onClick={this.handleClick}
              sx={{
                border: this.state.keycode === (48 + num).toString() ? '3px solid #ffd700' : undefined
              }}
            >
              {num}
            </NumberButton>
          ))}
          
          {/* Row 4: Delete, 0, Submit */}
          <ActionButton
            variant="delete"
            onClick={this.deleteNumber}
            startIcon={<BackspaceIcon />}
            sx={{
              border: this.state.keycode === '46' ? '3px solid #ffd700' : undefined,
              fontSize: '0.75rem'
            }}
          >
            Del
          </ActionButton>
          
          <NumberButton
            variant="zero"
            id="0"
            onClick={this.handleClick}
            sx={{
              border: this.state.keycode === '48' ? '3px solid #ffd700' : undefined
            }}
          >
            0
          </NumberButton>
          
          <ActionButton
            variant="enter"
            onClick={this.submitAnswer}
            endIcon={<SendIcon />}
            disabled={this.state.isSubmitting}
            sx={{
              border: this.state.keycode === '13' ? '3px solid #ffd700' : undefined,
              opacity: this.state.isSubmitting ? 0.6 : 1,
              fontSize: '0.75rem'
            }}
          >
            {this.state.isSubmitting ? '...' : 'Go'}
          </ActionButton>
        </Box>

        {/* Score Display */}
        {score > 0 && (
          <Chip 
            label={`Current Score: ${score.toLocaleString()}`}
            icon={<TrophyIcon />}
            sx={{ 
              background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
              color: '#333',
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}
          />
        )}
      </Box>
    );
   
    return(
      <GameContainer maxWidth={false}>
        {/* Header Section */}
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: 'white',
                fontWeight: 'bold',
                textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                mb: 1
              }}
            >
              🎯 Guess the Secret Number
            </Typography>
          </Box>
        </Fade>

        {/* Game Card */}
        <Slide in direction="up" timeout={800}>
          <GameCard elevation={0}>
            {/* Game Status */}
            <Box sx={{ mb: 2, textAlign: 'center' }}> {/* Reduced margin */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Typography variant="body1" sx={{ color: '#666' }}> {/* Smaller font and margin */}
                  🎲 Range: 0 - {lastNumber.toLocaleString()}
                </Typography>
                <Chip 
                  label={`Level ${level}`}
                  icon={<TrophyIcon />}
                  color="primary"
                  variant="filled"
                  size="small"
                  sx={{ 
                    background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                    color: '#333',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}> {/* Reduced gap */}
                <Chip 
                  label={`${count} attempts left`}
                  color={count <= 3 ? 'error' : count <= 5 ? 'warning' : 'success'}
                  variant="outlined"
                  size="small" // Added small size
                />
                <LinearProgress 
                  variant="determinate" 
                  value={(count / (8 + level - 1)) * 100}
                  sx={{ 
                    flex: 1, 
                    height: 6, // Reduced height
                    borderRadius: 4,
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Number Display */}
            <NumberDisplay>
              {count === 0 ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: '#ff6b6b', fontWeight: 'bold' }}>
                    Answer: {secretNumber}
                  </Typography>
                </Box>
              ) : number.length > 0 ? (
                number.join('')
              ) : (
                defaultText
              )}
            </NumberDisplay>

            {/* Game States */}
            {count === 0 ? (
              <Result 
                Answer={secretNumber} 
                newgame={this.newGame} 
                score={score} 
                rank={this.state.rank} 
                count={count}  
                user={userName} 
                toprankers={toprankers} 
                test={this.test}
              />
            ) : status === 'won' ? (
              <NextLevel 
                Answer={secretNumber} 
                level={level} 
                score={score} 
                count={count} 
                newgame={this.newGame} 
              />
            ) : userName === '' ? (
              renderUserNameDialog()
            ) : (
              /* Game Layout Container - Only for active gameplay */
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                minHeight: '320px',
                position: 'relative',
                pt: instructions.length > 0 ? '110px' : '0'
              }}>
                {/* Instructions/Hints - Fixed position */}
                {instructions.length > 0 && (
                  <Box sx={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    maxHeight: '100px',
                    minHeight: '60px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    zIndex: 2,
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    borderRadius: 2,
                    p: 1,
                    mb: 1,
                    border: '1px solid rgba(0,0,0,0.1)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(0,0,0,0.1)',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(102, 126, 234, 0.6)',
                      borderRadius: '3px',
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.8)',
                      },
                    },
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666', 
                        mb: 0.5, 
                        fontWeight: 'bold',
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        zIndex: 1
                      }}
                    >
                      🎯 Latest Hints ({instructions.length})
                    </Typography>
                    {instructions.slice(0, 5).map((inst, i) => (
                      <Typography 
                        key={i}
                        variant="body2"
                        sx={{ 
                          color: '#333',
                          fontSize: '0.7rem',
                          mb: 0.3,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          wordBreak: 'break-word',
                          lineHeight: 1.3
                        }}
                      >
                        <Chip 
                          label={instructions.length - i}
                          size="small"
                          sx={{ 
                            minWidth: '18px',
                            height: '16px',
                            fontSize: '0.6rem',
                            background: i === 0 ? '#667eea' : 'rgba(0,0,0,0.2)',
                            color: 'white'
                          }}
                        />
                        {inst.includes && inst.includes('smaller') && <TrendingDownIcon color="error" sx={{ fontSize: '14px' }} />}
                        {inst.includes && inst.includes('greater') && <TrendingUpIcon color="success" sx={{ fontSize: '14px' }} />}
                        {inst}
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Keypad Container - Centered with spacing */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                  py: 2
                }}>
                  <NumberPad />
                </Box>
              </Box>
            )}
          </GameCard>
        </Slide>
      </GameContainer>
    );
  }
}
export default GuessLevelOne;